/**
 * 应用工作台统一外壳（AppShell）
 *
 * 为各应用提供统一的系统级头部：返回入口、应用标识、账户簇（积分/登录/
 * 用户名/个人中心）与内置 AuthModal，消除各应用顶栏账户能力的重复实现
 * 与样式漂移。支持 light/dark 两种主题（dark 服务画布类暗色工作台）。
 *
 * 插槽约定：
 *  - headerRight：应用专属操作区（进度胶囊/生成/导出按钮等），排在账户簇左侧；
 *  - toolbar：桌面端居中工具栏（画布缩放/背景等）；
 *  - onOpenSidebar：提供时渲染移动端汉堡（lg:hidden）；
 *  - onOpenShortcuts：提供时渲染移动端快捷键按钮（lg:hidden）。
 *
 * 层级约定：头部 fixed z-50（工作台惯例），移动抽屉 z-[60] 之上不受影响。
 */

import { createContext, useContext, useState, type ReactNode } from 'react';
import { ChevronLeft, HelpCircle, Menu, Sparkles, UserRound, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthModal } from '../components/AuthModal';
import { usePlatformSession } from './session';

/** 工作区子组件可借此打开外壳内置的登录弹窗（如未登录 EmptyState 的登录按钮） */
const AppShellContext = createContext<{ openAuthModal: () => void } | null>(null);

export function useAppShell(): { openAuthModal: () => void } {
  const context = useContext(AppShellContext);
  if (!context) {
    throw new Error('useAppShell 必须在 AppShell 内使用');
  }
  return context;
}

/**
 * 受控登录弹窗：传入时外壳内置 AuthModal 改用外部 state（头部登录按钮
 * 与 EmptyState 等触发点共享同一弹窗），不传时外壳自管。
 */
export interface AppShellAuthModalControl {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSuccess: (user: { id: string; email: string; name: string | null; credits: number; apiKey: string }) => void;
}

interface AppShellProps {
  appName: string;
  appSubtitle?: string;
  appIcon?: ReactNode;
  backTo?: string;
  onOpenSidebar?: () => void;
  onOpenShortcuts?: () => void;
  headerRight?: ReactNode;
  toolbar?: ReactNode;
  theme?: 'light' | 'dark';
  /** workbench=满宽工作区；page=max-w-7xl 内容页 */
  layout?: 'workbench' | 'page';
  /** 覆盖外壳根节点背景类（默认 bg-light-bg） */
  backgroundClassName?: string;
  /** 受控登录弹窗（可选） */
  authModal?: AppShellAuthModalControl;
  children: ReactNode;
}

export function AppShell({
  appName,
  appSubtitle,
  appIcon,
  backTo = '/apps',
  onOpenSidebar,
  onOpenShortcuts,
  headerRight,
  toolbar,
  theme = 'light',
  layout = 'workbench',
  backgroundClassName = 'bg-light-bg',
  authModal,
  children,
}: AppShellProps) {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user, credits, isLoggedIn, setUser } = usePlatformSession();

  // 受控模式（authModal 提供）与自管模式共用同一渲染路径
  const authOpen = authModal ? authModal.open : showAuthModal;
  const openAuthModal = authModal ? authModal.onOpen : () => setShowAuthModal(true);
  const closeAuthModal = authModal ? authModal.onClose : () => setShowAuthModal(false);
  const handleAuthSuccess = authModal ? authModal.onSuccess : (nextUser: { id: string; email: string; name: string | null; credits: number; apiKey: string }) => setUser(nextUser);

  const isDark = theme === 'dark';

  const navClass = isDark
    ? 'fixed left-0 right-0 top-0 z-50 h-16 border-b border-slate-800 bg-slate-900/90 backdrop-blur-xl'
    : 'fixed left-0 right-0 top-0 z-50 border-b border-light-border bg-white/80 backdrop-blur-xl';
  const iconBtnClass = isDark
    ? 'rounded-xl p-2 text-slate-400 transition hover:bg-white/10 hover:text-slate-100'
    : 'rounded-lg px-2 py-1.5 text-sm text-light-muted transition-colors hover:bg-light-hover hover:text-light-text';
  const dividerClass = isDark ? 'hidden h-5 w-px bg-slate-700 sm:block' : 'hidden h-5 w-px bg-light-border sm:block';
  const logoClass = isDark
    ? 'flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-400 to-blue-600 text-white shadow-lg'
    : 'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-blue text-white shadow-sm';
  const titleClass = isDark ? 'text-sm font-semibold text-slate-100' : 'text-sm font-semibold text-light-text';
  const subtitleClass = isDark ? 'text-xs text-slate-400' : 'text-xs text-light-muted';
  const creditsClass = isDark
    ? 'hidden items-center gap-1.5 rounded-full bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 sm:flex'
    : 'hidden items-center gap-1.5 rounded-lg bg-white/70 px-2.5 py-1.5 text-light-text sm:flex';
  const userNameClass = isDark
    ? 'hidden max-w-[8rem] truncate text-sm text-slate-300 md:block'
    : 'hidden max-w-[8rem] truncate text-sm text-light-text md:block';
  const accountBtnClass = isDark
    ? 'flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm font-medium text-slate-200 transition hover:border-sky-400/50 hover:text-sky-300'
    : 'btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-sm';
  const loginBtnClass = isDark
    ? 'rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-sky-400'
    : 'btn-primary px-3 py-1.5 text-sm';

  return (
    <AppShellContext.Provider value={{ openAuthModal }}>
    <div className={`min-h-screen ${backgroundClassName}`} style={{ paddingTop: '64px' }}>
      <nav className={navClass}>
        <div className="mx-auto flex h-16 items-center justify-between gap-2 px-3 sm:px-6">
          {/* 左：返回 + 移动端侧栏开关 + 应用标识 */}
          <div className="flex min-w-0 items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => navigate(backTo)}
              className={`shrink-0 ${iconBtnClass}`}
              title="返回应用中心"
              aria-label="返回应用中心"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            {onOpenSidebar && (
              <button
                type="button"
                onClick={onOpenSidebar}
                className={`shrink-0 ${iconBtnClass} lg:hidden`}
                aria-label="打开项目库"
              >
                <Menu className="h-5 w-5" />
              </button>
            )}
            {onOpenShortcuts && (
              <button
                type="button"
                onClick={onOpenShortcuts}
                className={`shrink-0 ${iconBtnClass} lg:hidden`}
                aria-label="快捷键"
              >
                <HelpCircle className="h-5 w-5" />
              </button>
            )}
            <div className={dividerClass} />
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <div className={logoClass}>
                {appIcon ?? <Sparkles className="h-4 w-4" />}
              </div>
              <div className="min-w-0">
                <span className={`block truncate ${titleClass}`}>{appName}</span>
                {appSubtitle && (
                  <span className={`hidden truncate sm:block ${subtitleClass}`}>{appSubtitle}</span>
                )}
              </div>
            </div>
          </div>

          {/* 中：桌面端工具栏（画布缩放/背景等） */}
          {toolbar && (
            <div className="hidden flex-1 items-center justify-center gap-1 lg:flex">{toolbar}</div>
          )}

          {/* 右：应用操作区 + 账户簇 */}
          <div className="flex shrink-0 items-center gap-2">
            {headerRight}
            {isLoggedIn && (
              <div className={creditsClass}>
                <Zap className={isDark ? 'h-4 w-4' : 'h-3.5 w-3.5 text-accent-blue'} />
                <span className="whitespace-nowrap text-sm font-medium">{credits} 积分</span>
              </div>
            )}
            {isLoggedIn ? (
              <>
                <span className={userNameClass}>{user?.name}</span>
                <button
                  type="button"
                  onClick={() => navigate('/user')}
                  className={accountBtnClass}
                >
                  <UserRound className="h-4 w-4" />
                  <span className="hidden sm:inline">个人中心</span>
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={openAuthModal}
                className={loginBtnClass}
              >
                登录
              </button>
            )}
          </div>
        </div>
      </nav>

      <AuthModal
        isOpen={authOpen}
        onClose={closeAuthModal}
        onSuccess={handleAuthSuccess}
        defaultMode="login"
      />

      {layout === 'page' ? (
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      ) : (
        children
      )}
    </div>
    </AppShellContext.Provider>
  );
}
